import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface PermissionsProviderProps<TPermissionSet extends string> {
  getPermissions: () => Promise<TPermissionSet[]>;
  children: ReactNode;
}

export interface PermissionsContextProps<TPermissionSet extends string> {
  permissions: TPermissionSet[] | null;
  hasPermission: (permission: TPermissionSet) => boolean;
}

export const PermissionsContext = createContext<
  PermissionsContextProps<string>
>({
  permissions: null,
  hasPermission: () => false,
});

export const PermissionsProvider = <TPermissionSet extends string>(
  props: PermissionsProviderProps<TPermissionSet>
) => {
  const [permissions, setPermissions] = useState<TPermissionSet[] | null>(null);

  useEffect(() => {
    const init = async () => {
      if (!permissions) {
        const permissionList = await props.getPermissions();
        setPermissions([...permissionList]);
      }
    };

    init();
  }, [permissions]);

  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission as TPermissionSet) ?? false;
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        hasPermission,
      }}
    >
      {props.children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = <
  TPermissionSet extends string
>(): PermissionsContextProps<TPermissionSet> => {
  const context = useContext(
    PermissionsContext
  ) as unknown as PermissionsContextProps<TPermissionSet>;
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }

  return context;
};
